import { comoCampo } from "@/componentes/tesoreria/conversion"
import { aCentimos } from "@/componentes/tesoreria/dinero"

/** El reporte de cuentas del 2.0 tal como lo da `GET /publico/reportes/{token}`. */

export type Saldo = { moneda: string; saldo: string }

export type CuentaReporte = {
  id: string
  clase: string
  expediente: string
  concepto: string
  monto: string
  abonado: string
  saldo: string
  moneda: string
  emision: string | null
  vence: string | null
  antiguedad: number
  vencida: boolean
}

export type GrupoReporte = {
  tercero: string
  rif: string
  saldos: Saldo[]
  cuentas: CuentaReporte[]
}

export type Reporte = {
  empresa_nombre: string
  empresa_rif: string
  tipo: string
  titulo: string
  rotulo_tercero: string
  fecha: string
  generado_en: string
  expira_en: string
  total_cuentas: number
  saldos: Saldo[]
  vencidas: number
  saldos_vencidos: Saldo[]
  tramos: { rotulo: string; saldos: Saldo[] }[]
  grupos: GrupoReporte[]
}

/** «cliente(s)» pero «proveedor(es)»: el plural no es siempre con s. */
export function plural(rotulo: string): string {
  const r = rotulo.toLowerCase()
  return /[aeiou]$/.test(r) ? `${r}(s)` : `${r}(es)`
}

export const CLASE: Record<string, string> = {
  LIQUIDACION: "Liquidación",
  GASTO_FINANCIADO: "Gasto financiado",
  OPERATIVA: "Operativa",
  // (22/10) Las administrativas del 2.0 dicen de dónde nacen.
  ADMIN_EXPEDIENTE: "Administrativa del expediente",
  SERVICIO_PROPIO: "Servicio propio",
  ADMINISTRATIVA: "Administrativa",
}

/**
 * El saldo por ORIGEN de la cuenta —operativa, administrativa del expediente, servicio
 * propio…—, por moneda (22/10). Se calcula de las cuentas del reporte, así que vale
 * también para las fotos ya emitidas. Una sola clase no se resume: no dice nada.
 */
export function porOrigen(r: Reporte): { clase: string; rotulo: string; saldos: Saldo[] }[] {
  const acumulado = new Map<string, Map<string, bigint>>()
  for (const g of r.grupos) {
    for (const c of g.cuentas) {
      const porMoneda = acumulado.get(c.clase) ?? new Map<string, bigint>()
      porMoneda.set(c.moneda, (porMoneda.get(c.moneda) ?? 0n) + aCentimos(c.saldo))
      acumulado.set(c.clase, porMoneda)
    }
  }
  if (acumulado.size < 2) return []
  // En el orden de `CLASE`; una clase que no conoce va al final.
  const orden = Object.keys(CLASE)
  const puesto = (clase: string) => {
    const i = orden.indexOf(clase)
    return i < 0 ? orden.length : i
  }
  return [...acumulado.entries()]
    .sort(([a], [b]) => puesto(a) - puesto(b))
    .map(([clase, monedas]) => ({
      clase,
      rotulo: CLASE[clase] ?? clase,
      saldos: [...monedas.entries()].map(([moneda, c]) => ({ moneda, saldo: comoCampo(c) })),
    }))
}
